import React, { useState, useEffect } from 'react';
import { MagneticButton } from '../common/MagneticButton';

export const HeaderNav: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [co2Reading, setCo2Reading] = useState(419.8);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 30);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });

    const interval = setInterval(() => {
      setCo2Reading((prev) => +(prev + (Math.random() - 0.5) * 0.04).toFixed(2));
    }, 4000);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearInterval(interval);
    };
  }, []);

  const navLinks = [
    { label: '01. BIOSPHERE', href: '#hero' },
    { label: '02. MANIFESTO', href: '#manifesto' },
    { label: '03. PILLARS', href: '#pillars' },
    { label: '04. ARCHITECTURE', href: '#architecture' },
    { label: '05. SIMULATOR', href: '#simulator' },
    { label: '06. BENCHMARKS', href: '#benchmarks' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        scrolled
          ? 'bg-alabaster-100/90 backdrop-blur-md border-b border-carbon-900/10 shadow-sm py-3'
          : 'bg-transparent border-b border-carbon-900/10 py-4 sm:py-5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between">
        {/* Brand Architecture Monogram */}
        <a
          href="#hero"
          className="flex items-center gap-3.5 group shrink-0"
          data-cursor="interactive"
        >
          <div className="w-8 h-8 rounded-none border border-carbon-900 bg-carbon-900 text-alabaster-100 flex items-center justify-center font-serif text-sm font-bold tracking-wider group-hover:bg-chlorophyll group-hover:border-chlorophyll transition-colors">
            G
          </div>
          <div>
            <div className="font-serif font-bold text-sm tracking-wider text-carbon-900 flex items-center gap-2">
              <span>GAIA PULSE</span>
              <span className="text-[10px] font-mono font-normal text-chlorophyll-deep border border-chlorophyll/30 px-1.5 py-0.2 bg-chlorophyll/5">
                SPECIMEN 02
              </span>
            </div>
            <div className="text-[9px] font-mono text-carbon-900/50 uppercase tracking-widest hidden sm:block">
              LIVING BIOSPHERE CODEX
            </div>
          </div>
        </a>

        {/* Desktop Navigation Links */}
        <nav className="hidden 2xl:flex items-center gap-6 font-mono text-xs">
          {navLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              className="text-carbon-900/70 hover:text-chlorophyll-deep transition-colors tracking-widest text-[11px] relative py-1 after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-0 after:h-[1px] after:bg-chlorophyll after:transition-all hover:after:w-full"
              data-cursor="interactive"
            >
              {link.label}
            </a>
          ))}
        </nav>

        {/* Right Area: Atmospheric Ticker & CTA */}
        <div className="flex items-center gap-3 sm:gap-4 shrink-0">
          {/* Live CO2 Telemetry Ticker */}
          <div className="hidden sm:flex items-center gap-2 px-3 py-1 border border-carbon-900/15 bg-white font-mono text-[10px] text-carbon-900/70">
            <span className="w-1.5 h-1.5 rounded-full bg-chlorophyll animate-pulse" />
            <span className="font-bold text-carbon-900">{co2Reading} PPM</span>
            <span className="text-chlorophyll-deep font-semibold">STABLE</span>
          </div>

          <MagneticButton
            href="#simulator"
            variant="primary"
            size="sm"
            showArrow={true}
            className="hidden sm:inline-flex"
          >
            Initiate Audit
          </MagneticButton>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden w-9 h-9 border border-carbon-900/20 bg-white flex items-center justify-center text-carbon-900 focus:outline-none"
            aria-label="Toggle Navigation Menu"
            data-cursor="interactive"
          >
            <div className="w-4 flex flex-col items-center gap-1">
              <span
                className={`w-full h-[1.5px] bg-carbon-900 transition-all duration-300 ${
                  mobileMenuOpen ? 'rotate-45 translate-y-1.5' : ''
                }`}
              />
              <span
                className={`w-full h-[1.5px] bg-carbon-900 transition-all duration-300 ${
                  mobileMenuOpen ? 'opacity-0' : ''
                }`}
              />
              <span
                className={`w-full h-[1.5px] bg-carbon-900 transition-all duration-300 ${
                  mobileMenuOpen ? '-rotate-45 -translate-y-1.5' : ''
                }`}
              />
            </div>
          </button>
        </div>
      </div>

      {/* Mobile Drawer Overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-x-0 top-full bg-alabaster-100 border-b border-carbon-900/15 p-6 shadow-xl space-y-6">
          <div className="space-y-3 font-serif">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={() => setMobileMenuOpen(false)}
                className="block text-xl font-bold text-carbon-900 hover:text-chlorophyll transition-colors py-1"
              >
                {link.label}
              </a>
            ))}
          </div>

          <div className="pt-4 border-t border-carbon-900/10 flex items-center justify-between font-mono text-xs text-carbon-900/70">
            <span>ATMOSPHERIC CARBON:</span>
            <span className="font-bold text-chlorophyll-deep">{co2Reading} PPM CO2</span>
          </div>

          <MagneticButton
            href="#simulator"
            variant="primary"
            size="md"
            className="w-full justify-center"
            onClick={() => setMobileMenuOpen(false)}
          >
            Initiate Audit ↗
          </MagneticButton>
        </div>
      )}
    </header>
  );
};
